import Core from '../plugin-core-system/core.class.js';

export default class CsGlobal extends Core {
  static options = {};

  init() {
    const nav = document.querySelector('#nav');
    this.retryPatchEasyDropDown({
      attempts: 20,
      delay: 200,
      lastPatchedCount: 0,
      stableRuns: 0,
      stableStopAfter: 2
    });

    this.patchTopNavFlyouts(nav);
    this.patchFlyoutNestedSubmenus(nav);
    //this.patchMobileNavClose(nav);
  }

  patchMobileNavClose(navEl) {
    if (!navEl) return;
    if (navEl.dataset.mobileClosePatched === '1') return;
    navEl.dataset.mobileClosePatched = '1';

    const OPEN_CLASS = 'hover';

    const isOpen = () => navEl.classList.contains(OPEN_CLASS);

    const closeNav = () => {
      if (!isOpen()) return;
      navEl.classList.remove(OPEN_CLASS);

      // Optional: also close any open nested flyouts (legacy "hover" etc.)
      navEl.querySelectorAll('li.hover').forEach((li) => li.classList.remove('hover'));
      navEl.querySelectorAll('[aria-expanded="true"]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    };

    // 1) ESC anywhere inside nav closes
    navEl.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeNav();

      // Put focus back to burger icon if present (better UX)
      const burger = navEl.querySelector(':scope > a.cs-icon-menu');
      if (burger) burger.focus();
    });

    // 2) When focus leaves nav, close (prevents tabbing into hidden page)
    navEl.addEventListener('focusout', (e) => {
      const next = e.relatedTarget;
      if (next && navEl.contains(next)) return; // still inside nav
      closeNav();
    });

    // 3) Click outside closes (good for touch + mouse)
    document.addEventListener('mousedown', (e) => {
      if (!isOpen()) return;
      if (navEl.contains(e.target)) return;
      closeNav();
    });
  }

  patchFlyoutNestedSubmenus(navEl) {
    if (!navEl) return;

    const itemSelector = '.fly-out li';
    const items = Array.from(navEl.querySelectorAll(itemSelector)).filter((li) => {
      const trigger = li.querySelector(':scope > a');
      const submenu = li.querySelector(':scope > ul.list-links');
      return trigger && submenu;
    });

    items.forEach((li, idx) => {
      if (li.dataset.flyout3Patched === '1') return;
      li.dataset.flyout3Patched = '1';

      const trigger = li.querySelector(':scope > a');
      const submenu = li.querySelector(':scope > ul.list-links');
      if (!trigger || !submenu) return;

      // Ensure submenu has an id for aria-controls
      if (!submenu.id) submenu.id = `flyout-submenu-${idx + 1}`;

      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', li.classList.contains('hover') ? 'true' : 'false');
      trigger.setAttribute('aria-controls', submenu.id);

      const isOpen = () => li.classList.contains('hover');

      const open = () => {
        li.classList.add('hover');
        trigger.setAttribute('aria-expanded', 'true');
      };

      const close = () => {
        li.classList.remove('hover');
        trigger.setAttribute('aria-expanded', 'false');
      };

      // Close siblings inside the same list (optional but usually desired on mobile)
      const closeSiblings = () => {
        const parentList = li.parentElement;
        if (!parentList) return;
        Array.from(parentList.children).forEach((sib) => {
          if (sib === li) return;
          if (!(sib instanceof HTMLElement)) return;
          if (!sib.classList.contains('hover')) return;
          sib.classList.remove('hover');
          const sibA = sib.querySelector(':scope > a');
          if (sibA) sibA.setAttribute('aria-expanded', 'false');
        });
      };

      const handleActivate = (e) => {
        // First activation opens, second allows navigation
        if (!isOpen()) {
          e.preventDefault();
          closeSiblings();
          open();
        } else {
          // If Space on <a>, browsers may scroll; simulate click to ensure navigation
          if (e.type === 'keydown' && e.key === ' ') {
            e.preventDefault();
            trigger.click();
          }
          // Otherwise allow default navigation
        }
      };

      trigger.addEventListener('click', handleActivate);

      trigger.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        handleActivate(e);
      });

      // Optional: open on focus (this is what you asked: "öffnet nicht per focus")
      trigger.addEventListener('focus', () => {
        closeSiblings();
        open();
      });

      // ESC closes when focus is within the li/submenu
      li.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        e.preventDefault();
        close();
        trigger.focus();
      });
    });
  }

  patchTopNavFlyouts(navEl) {
    if (!navEl) return;
    if (navEl.dataset.flyoutPatched === '1') return;
    navEl.dataset.flyoutPatched = '1';

    const openClass = 'is-flyout-open';

    const getTopItems = () =>
      Array.from(navEl.querySelectorAll(':scope > ul > li')).filter((li) =>
        li.querySelector(':scope > a') && li.querySelector(':scope > .fly-out')
      );

    const closeAll = () => {
      getTopItems().forEach((li) => this.closeFlyoutItem(li, openClass));
    };

    const closeAllExcept = (keepLi) => {
      getTopItems().forEach((li) => {
        if (li !== keepLi) this.closeFlyoutItem(li, openClass);
      });
    };

    // Init ARIA + ids
    getTopItems().forEach((li, idx) => {
      const trigger = li.querySelector(':scope > a');
      const flyout = li.querySelector(':scope > .fly-out');
      if (!trigger || !flyout) return;

      if (!flyout.id) {
        flyout.id = `flyout-${idx + 1}`;
      }

      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', flyout.id);
    });

    // Key handling on top-level triggers
    navEl.addEventListener('keydown', (e) => {
      const isActivationKey = e.key === 'Enter' || e.key === ' ';
      const isEscape = e.key === 'Escape';
      if (!isActivationKey && !isEscape) return;

      const trigger = e.target && e.target.closest('#nav > ul > li > a');
      if (!trigger) return;

      const li = trigger.parentElement;
      const flyout = li && li.querySelector(':scope > .fly-out');
      if (!li || !flyout) return;

      if (isEscape) {
        e.preventDefault();
        this.closeFlyoutItem(li, openClass);
        trigger.focus();
        return;
      }

      // Enter/Space:
      // If closed => open and prevent navigation
      // If open  => allow default (navigation)
      const isOpen = li.classList.contains(openClass) || trigger.getAttribute('aria-expanded') === 'true';

      if (!isOpen) {
        e.preventDefault();
        closeAllExcept(li);
        this.openFlyoutItem(li, openClass);
      } else {
        // Open already -> allow navigation (do NOT preventDefault)
        // But if key was Space, browsers may scroll; we still want it to "activate".
        // Space on <a> isn't consistently "click" in all browsers, so we simulate.
        if (e.key === ' ') {
          e.preventDefault();
          trigger.click();
        }
      }
    });

    // Click on top-level triggers: optional same behavior as keyboard (first click opens)
    // If you ONLY want keyboard behavior, you can remove this block.
    navEl.addEventListener('click', (e) => {
      const trigger = e.target && e.target.closest('#nav > ul > li > a');
      if (!trigger) return;

      const li = trigger.parentElement;
      const flyout = li && li.querySelector(':scope > .fly-out');
      if (!li || !flyout) return;

      const isOpen = li.classList.contains(openClass) || trigger.getAttribute('aria-expanded') === 'true';

      if (!isOpen) {
        e.preventDefault();
        closeAllExcept(li);
        this.openFlyoutItem(li, openClass);
      } else {
        // second click navigates normally
        closeAll();
      }
    });

    // Close when focus leaves the whole nav
    navEl.addEventListener('focusin', (e) => {
      const trigger = e.target && e.target.closest('#nav > ul > li > a');
      if (!trigger) return;

      const li = trigger.parentElement;
      const flyout = li && li.querySelector(':scope > .fly-out');
      if (!li || !flyout) return;

      // Set ARIA first to avoid NVDA announcing "collapsed"
      trigger.setAttribute('aria-expanded', 'true');

      closeAllExcept(li);
      this.openFlyoutItem(li, openClass);
    }, true);

    navEl.addEventListener('focusout', (e) => {
      const next = e.relatedTarget;
      if (next && navEl.contains(next)) return; // focus still inside nav
      closeAll();
    });

    // Close on outside click
    document.addEventListener('mousedown', (e) => {
      if (navEl.contains(e.target)) return;
      closeAll();
    });
  }

  openFlyoutItem(li, openClass) {
    const trigger = li.querySelector(':scope > a');
    if (!trigger) return;

    li.classList.add(openClass);
    trigger.setAttribute('aria-expanded', 'true');
  }

  closeFlyoutItem(li, openClass) {
    const trigger = li.querySelector(':scope > a');
    if (!trigger) return;

    li.classList.remove(openClass);
    trigger.setAttribute('aria-expanded', 'false');
  }

  retryPatchEasyDropDown({ attempts, delay, lastPatchedCount, stableRuns, stableStopAfter }) {
    const patchedCount = this.patchEasyDropDown(document);
    const didPatchNew = patchedCount > lastPatchedCount;
    const nextStableRuns = didPatchNew ? 0 : stableRuns + 1;

    if (attempts <= 0) return;
    if (nextStableRuns >= stableStopAfter) return;

    window.setTimeout(() => {
      this.retryPatchEasyDropDown({
        attempts: attempts - 1,
        delay,
        lastPatchedCount: patchedCount,
        stableRuns: nextStableRuns,
        stableStopAfter
      });
    }, delay);
  }

  /**
   * Minimal patch:
   * - Make dropdown focusable
   * - Enter (and Space) triggers the same open behavior as mouse click
   */
  patchEasyDropDown(rootEl) {
    const root = rootEl || document;

    const dropdowns = Array.from(root.querySelectorAll('.dropdown'));
    dropdowns.forEach((dropdown) => {
      // Only patch EasyDropDown instances with the expected structure
      const selected = dropdown.querySelector('span.selected');
      const select = dropdown.querySelector('span.old > select');

      if (!selected || !select) return;

      // Avoid patching twice
      if (dropdown.dataset.a11yPatched === '1') return;
      dropdown.dataset.a11yPatched = '1';

      // Make focusable
      dropdown.tabIndex = 0;

      select.tabIndex = -1;
      select.setAttribute('aria-hidden', 'true');

      // Enter/Space opens (no other key handling!)
      dropdown.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;

        e.preventDefault();
        selected.click();
      });
    });

    return document.querySelectorAll('.dropdown[data-a11y-patched="1"]').length;
  }
}
